import { useEffect, useState } from 'react'
import { THEATER_CONFIGS, TheaterId } from '@fleetduel/shared'
import { useTheaterStore } from '../../store/theaterStore.js'
import { useAudio } from '../../hooks/useAudio.js'
import { trackEvent } from '../../utils/analytics.js'
import { REEL_SEGMENTS } from '../../assets/index.js'

/** Map TheaterId → reel poster src */
const THEATER_POSTERS: Record<Exclude<TheaterId, 'classic'>, string> = {
  hormuz: REEL_SEGMENTS.find(s => s.id === 'hormuz')!.src,
  giuk: REEL_SEGMENTS.find(s => s.id === 'giuk')!.src,
  'south-china-sea': REEL_SEGMENTS.find(s => s.id === 'scs')!.src,
}

interface TheaterSelectProps {
  onStart: () => void
  onBack: () => void
}

/** Hormuz, Greenland, and South China Sea are playable. */
const PLAYABLE_THEATERS: TheaterId[] = ['hormuz', 'giuk', 'south-china-sea']

/** Theater accent colors (ART_GUIDE_v3 §1.2) */
const THEATER_ACCENTS: Record<Exclude<TheaterId, 'classic'>, string> = {
  hormuz: '#F4B942',
  giuk: '#8DA9C4',
  'south-china-sea': '#3DDC97',
}

/** Pin positions on the 360×200 world map viewBox */
const PIN_POSITIONS: Record<Exclude<TheaterId, 'classic'>, { x: number; y: number; label: string; labelDx: number }> = {
  giuk: { x: 148, y: 42, label: 'Greenland', labelDx: 10 },
  hormuz: { x: 219, y: 93, label: 'Hormuz', labelDx: 10 },
  'south-china-sea': { x: 283, y: 106, label: 'S. China Sea', labelDx: -78 },
}

const PIN_IDS = Object.keys(PIN_POSITIONS) as (keyof typeof PIN_POSITIONS)[]

/**
 * Theater Select screen (ART_GUIDE_v3 §1, GDD-v3 §2).
 * Last stop before the game: START launches straight into placement
 * (difficulty adapts to the player's unlock record — no difficulty screen).
 * Classic is the default selection; pins select real-world theaters.
 */
export function TheaterSelect({ onStart, onBack }: TheaterSelectProps) {
  const theaterId = useTheaterStore(s => s.theaterId)
  const setTheater = useTheaterStore(s => s.setTheater)
  const setTerrainEnabled = useTheaterStore(s => s.setTerrainEnabled)
  const { play } = useAudio()

  const [comingSoon, setComingSoon] = useState<TheaterId | null>(null)

  // Terrain is always on now; Hormuz is the default (and only) playable theater
  useEffect(() => {
    setTerrainEnabled(true)
    if (!PLAYABLE_THEATERS.includes(theaterId)) setTheater('hormuz')
  }, [theaterId, setTheater, setTerrainEnabled])

  const selectPin = (id: TheaterId) => {
    play('button-press', 'ui')
    if (!PLAYABLE_THEATERS.includes(id)) {
      // Not playable yet — tease instead of selecting
      setComingSoon(id)
      // Demand signal: which locked theaters players are tapping
      trackEvent('theater_coming_soon_tap', { theater: id })
      return
    }
    setComingSoon(null)
    setTheater(id)
    trackEvent('theater_selected', { theater: id })
  }

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      overflow: 'hidden',
      backgroundColor: '#0B2545',
    }}>
      <style>{`
        @keyframes theater-pin-pulse {
          0%, 100% { opacity: 0.35; r: 12; }
          50% { opacity: 0.1; r: 16; }
        }
      `}</style>

      {/* Theater poster backgrounds — all mounted, opacity transitions on selection */}
      <div aria-hidden style={{ position: 'absolute', inset: 0 }}>
        {PLAYABLE_THEATERS.map(id => (
          <img
            key={id}
            src={THEATER_POSTERS[id as keyof typeof THEATER_POSTERS]}
            alt=""
            draggable={false}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: theaterId === id ? 1 : 0,
              transition: 'opacity 500ms ease-in-out',
            }}
          />
        ))}
      </div>

      {/* Scrim: heavier than splash so the map + UI stays legible */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(to bottom,
            rgba(11,37,69,0.55) 0%,
            rgba(11,37,69,0.45) 40%,
            rgba(11,37,69,0.65) 100%)`,
        }}
      />

      {/* Content layer */}
      <div style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '16px 20px 24px',
        gap: 16,
        maxWidth: 760,
        margin: '0 auto',
      }}>

      {/* Header */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
        <button
          onClick={() => { play('button-press', 'ui'); onBack() }}
          style={{
            background: 'none',
            border: 'none',
            color: '#8DA9C4',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: 1,
          }}
        >
          ◂ BACK
        </button>
      </div>

      <div
        style={{
          textAlign: 'center',
          padding: '14px 28px',
          borderRadius: 10,
          backgroundColor: 'rgba(11,37,69,0.72)',
          border: '1px solid rgba(141,169,196,0.4)',
          boxShadow: '0 4px 20px rgba(11,37,69,0.5)',
          backdropFilter: 'blur(2px)',
        }}
      >
        <h1
          style={{
            fontSize: 30,
            fontWeight: 800,
            letterSpacing: 4,
            color: '#EAF2F8',
            textShadow: '0 2px 10px rgba(11,37,69,0.9)',
          }}
        >
          SELECT THEATER
        </h1>
        <div
          style={{
            width: 48,
            height: 3,
            backgroundColor: '#F4B942',
            borderRadius: 2,
            margin: '8px auto 0',
          }}
        />
        <p style={{ fontSize: 12, letterSpacing: 1, color: '#A6C6E1', marginTop: 8 }}>
          Choose your battleground
        </p>
      </div>

      {/* World map (ART_GUIDE_v3 §1.2: flat silhouettes, #1D4E89 on #0B2545) */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          padding: 8,
          borderRadius: 12,
          backgroundColor: 'rgba(11,37,69,0.72)',
          border: '1px solid rgba(141,169,196,0.4)',
          boxShadow: '0 4px 20px rgba(11,37,69,0.5)',
          backdropFilter: 'blur(2px)',
        }}
      >
      <svg
        viewBox="0 0 360 200"
        style={{
          width: '100%',
          display: 'block',
          backgroundColor: 'rgba(11, 37, 69, 0.35)',
          border: '1px solid rgba(141, 169, 196, 0.28)',
          borderRadius: 8,
        }}
        role="group"
        aria-label="World map theater selection"
      >
        <g fill="#1D4E89">
          {/* North America */}
          <path d="M28 34 L96 26 L112 42 L104 60 L118 72 L96 92 L74 88 L58 100 L46 82 L30 66 Z" />
          {/* Greenland */}
          <path d="M118 18 L144 14 L150 30 L136 44 L120 36 Z" />
          {/* South America */}
          <path d="M88 104 L112 100 L120 122 L110 156 L98 176 L90 148 L82 120 Z" />
          {/* Europe */}
          <path d="M156 44 L188 36 L198 52 L186 66 L166 72 L154 60 Z" />
          {/* Africa */}
          <path d="M158 80 L196 76 L210 96 L204 126 L186 156 L172 138 L160 106 Z" />
          {/* Asia */}
          <path d="M198 30 L280 22 L326 40 L318 68 L296 88 L272 96 L252 84 L228 88 L212 70 L202 52 Z" />
          {/* SE Asia / Indonesia */}
          <path d="M276 112 L300 108 L314 120 L296 128 L278 124 Z" />
          {/* Australia */}
          <path d="M296 142 L332 138 L340 158 L322 172 L300 164 Z" />
        </g>

        {/* Theater pins */}
        {PIN_IDS.map(id => {
          const pin = PIN_POSITIONS[id]
          const color = THEATER_ACCENTS[id]
          const selected = theaterId === id
          return (
            <g
              key={id}
              onClick={() => selectPin(id)}
              style={{ cursor: 'pointer' }}
              role="button"
              aria-label={`Select ${THEATER_CONFIGS[id].name}`}
              aria-pressed={selected}
            >
              {/* Pulse halo */}
              <circle
                cx={pin.x}
                cy={pin.y}
                r={12}
                fill={color}
                style={{ animation: 'theater-pin-pulse 2s ease-in-out infinite' }}
              />
              {/* Pin core — selected scales to 1.3× (ART_GUIDE_v3 §1.2) */}
              <circle
                cx={pin.x}
                cy={pin.y}
                r={selected ? 7.8 : 6}
                fill={color}
                stroke={selected ? '#EAF2F8' : 'none'}
                strokeWidth={selected ? 1.5 : 0}
                style={{ transition: 'r 150ms ease-out' }}
              />
              <text
                x={pin.x + pin.labelDx}
                y={pin.y + 4}
                fill={selected ? '#EAF2F8' : '#8DA9C4'}
                fontSize={11}
                fontWeight={selected ? 700 : 400}
              >
                {pin.label}
              </text>
            </g>
          )
        })}
      </svg>

      {/* Coming-soon overlay for theaters that aren't playable yet */}
      {comingSoon && (
        <div
          onClick={() => setComingSoon(null)}
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 8,
            backgroundColor: 'rgba(11, 37, 69, 0.82)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            cursor: 'pointer',
          }}
        >
          <div style={{
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: 2,
            color: '#EAF2F8',
          }}>
            {THEATER_CONFIGS[comingSoon].name.toUpperCase()}
          </div>
          <div style={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 3,
            color: THEATER_ACCENTS[comingSoon as Exclude<TheaterId, 'classic'>],
            textTransform: 'uppercase',
          }}>
            Coming Soon
          </div>
          <div style={{ fontSize: 11, color: '#8DA9C4', marginTop: 4 }}>
            Tap to dismiss
          </div>
        </div>
      )}
      </div>

      {/* Bottom actions */}
      <div style={{
        width: '100%',
        display: 'flex',
        gap: 12,
        marginTop: 'auto',
      }}>
        <button
          onClick={() => { play('button-press', 'ui'); onStart() }}
          style={{
            flex: 1,
            height: 48,
            backgroundColor: '#F4B942',
            border: 'none',
            borderRadius: 8,
            color: '#0B2545',
            fontSize: 16,
            fontWeight: 800,
            letterSpacing: 1,
            cursor: 'pointer',
          }}
        >
          ▶ START
        </button>
      </div>
      </div>{/* end content layer */}
    </div>
  )
}
